import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["tableComponent", "columns", "onCollapseColumns", "columnsCollapseOrder", "data", "paginatedData"],
      _excluded2 = ["fixed", "onCollapseColumns"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import debounce from "lodash.debounce";
import PropTypes from "prop-types";
import React, { useLayoutEffect, useRef, useState } from "react";
import { ACTION_COLUMN_PREFIX, RESIZE_DEBOUNCE_TIME_MS, RESPONSIVE_MINIMUM_COLUMNS_ALLOWED } from "../constants";
import extractTheme from "./extractTheme";
import onWidthResize from "./onWidthResize";
import { calculateMinimumDisplayableColumns, flattenCollapseOrder, getAllColumnsWidth, getDisplayableColumnCount, processColumnsOrder, reorderColumnsByCollapseOrder } from "./utilities";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
const TABLE_DEFINED_COLUMNS_REGEX = new RegExp(`^${ACTION_COLUMN_PREFIX}`);
/**
 * @summary A higher-order component (HOC) to add responsiveness to its child;
 *          now targeted at [Table](#table).
 * @type {Component}
 * @desc Responsiveness is implemented by binding resize events to trigger calculation of displayable columns
 */

const Responsive = /*#__PURE__*/React.forwardRef((_ref, tableNodeRefProp) => {
  let {
    tableComponent: TableComponent,
    columns: columns,
    onCollapseColumns,
    columnsCollapseOrder,
    data,
    // eslint-disable-next-line react/prop-types
    paginatedData
  } = _ref,
      tableProps = _objectWithoutProperties(_ref, _excluded);

  const internalRef = useRef();
  const tableNodeRef = tableNodeRefProp || internalRef;
  /** Instance variable to keep the minimum initial widths in a displayed table with width of 1px. */

  const columnWidths = useRef([]);
  /** Array of columns displayable in expandable container. */

  const collapsedColumns = useRef([]);
  /** Stores the columns passed to Table via `columns` prop ordered by collapse order */

  const reorderedColumnsByCollapseOrder = useRef([]);
  /**
   * `columnsCollapseOrder` can contain a double array, to collapse a set of columns together.
   * Stores the flattened array, in order to minimize,
   * the number of calculations needed to get the collapsed columns.
   */

  const flattenedCollapseOrder = useRef([]);
  /** Stores processed, ordered and displayable columns.  */

  const orderedColumns = useRef([]);
  const reorderedColumnWidths = useRef();
  const tableContainerWidth = useRef();
  /** Flag to determine if the table's container is visible. */

  const tableContainerNodeIsVisible = useRef(false);
  const displayableColumnCountLocal = useRef();
  const calculatedCollapsedColumns = useRef([]);
  const tableHasCollapseOrder = useRef(columnsCollapseOrder && columnsCollapseOrder.length > 0);
  const tableContainerWidthObserver = useRef();
  /** Stores the minimum number of columns to be displayed. */

  let minimumColumnsRequired = RESPONSIVE_MINIMUM_COLUMNS_ALLOWED;
  /** Flag to determine if the minimum width of each column has been measured. */

  const [columnsMinWidthMeasured, setColumnsMinWidthMeasured] = useState(false);
  /** Number of columns displayable in a parent container: initial set to count of all columns. */

  const [displayableColumnCount, setDisplayableColumnCount] = useState(columns.length);
  const prevProps = useRef({
    columnsCollapseOrder,
    data,
    columns,
    paginatedData
  });
  const prevState = useRef({
    columnsMinWidthMeasured,
    displayableColumnCount
  });
  const theme = useTheme();
  const {
    responsiveStyles
  } = extractTheme({
    theme
  });
  /**
   * Record reordered column widths to pass to getDisplayableColumnCount
   * in the case that a collapse order is set.
   */

  const setReOrderedHeaderColumnWidths = () => {
    reorderedColumnWidths.current = [];
    reorderedColumnsByCollapseOrder.current.forEach(reorderedColumn => {
      reorderedColumnWidths.current.push(columnWidths.current[reorderedColumn.originalIndex]);
    });
  };
  /**
   * Calculates the number of columns that can fit in the Table's container.
   */


  const getDisplayableColumnsCount = () => {
    const tableNode = tableNodeRef.current;

    if (!columnsMinWidthMeasured) {
      const headerColumns = tableNode.querySelector("thead tr").children;
      const columnWidthsLocal = getAllColumnsWidth(headerColumns);
      columnWidths.current = columnWidthsLocal;

      if (tableHasCollapseOrder.current) {
        setReOrderedHeaderColumnWidths();
      }
    }

    return getDisplayableColumnCount(reorderedColumnWidths.current || columnWidths.current, tableContainerWidth.current, minimumColumnsRequired);
  };
  /**
   * Returns the visible columns to be displayed
   * in the Table Header that are ordered by
   * collapse order.
   */


  const getVisibleColumnsOrderedByCollapseOrder = () => {
    let index;
    const columnsLength = columns.length;
    const tableDefinedColumns = [];
    /**
     * Loop through the columns array passed as a prop to this component
     * and push only the columns defined by the Table component
     * eg: `pnr-rownumber`, `pnr-selectable`, etc.
     * into the `tableDefinedColumns` array.
     */

    for (index = 0; index < columnsLength; index++) {
      const column = columns[index];

      if (!TABLE_DEFINED_COLUMNS_REGEX.test(column.key)) {
        break;
      }

      tableDefinedColumns.push(column);
    }
    /**
     * Table defined columns change per render of the component
     * depending on whether a page change or sorting or selection
     * has occurred due to the user interaction with the Table.
     *
     * But, since the Table defined columns for the columns ordered by collapse order
     * remain constant if the columns displayable count hasn't changed,
     * merge the columns ordered by collapse order with the
     * columns defined by the Table in order to
     * always display the latest updates made to the user defined columns.
     */


    const mergedColumns = _objectSpread(_objectSpread({}, orderedColumns.current), tableDefinedColumns);
    /**
     * Converting object of objects into an array of objects.
     */


    const mergedColumnsKeys = Object.keys(mergedColumns);
    const mergedColumnsAsArray = mergedColumnsKeys.map(key => mergedColumns[key]);
    return mergedColumnsAsArray;
  };
  /**
   * Calculates flattenedCollapseOrder, reorderedColumnsByCollapseOrder and
   * minimumColumnsRequired based on the columns and columnsCollapseOrder
   * prop value.
   * @param {array} columns Columns to be rendered.
   * @param {array} columnsCollapseOrder The order at which the columns collapse.
   */


  const calculateCollapsedColumnsOrder = (columns, columnsCollapseOrder) => {
    tableHasCollapseOrder.current = columnsCollapseOrder && columnsCollapseOrder.length > 0; // Calculate collapse order  related instance variables if table has a collapse order config.

    if (tableHasCollapseOrder.current) {
      // Storing the flattened columnsCollapseOrder prop.
      flattenedCollapseOrder.current = flattenCollapseOrder(columns.length, columnsCollapseOrder); // Storing columns reordered with collapse order priority.

      reorderedColumnsByCollapseOrder.current = reorderColumnsByCollapseOrder(columns, flattenedCollapseOrder.current);
    }

    minimumColumnsRequired = calculateMinimumDisplayableColumns(columns);
  };
  /**
   * Compares 2 Arrays of data Objects to determine if they are same.
   * @param {Object[]} data1
   * @param {Object[]} data2
   * @return {Boolean} `true` if each Array of data are same.
   */


  const isSameData = (data1, data2) => JSON.stringify(data1) === JSON.stringify(data2);
  /**
   * It checks for multiple changes to know if we need to trigger
   * the responsive logic.
   * @param {Object} prevProps
   * @param {Object} prevState
   */


  const shouldHandleResponsiveness = () => {
    const preProps = prevProps.current;
    const preState = prevState.current;

    if (!tableContainerNodeIsVisible.current) {
      return false;
    } // When the columns min width needs to be measured again.


    if (preState.columnsMinWidthMeasured && !columnsMinWidthMeasured) {
      return true;
    } // eslint-disable-next-line react/prop-types


    if (preProps.data !== data) {
      prevProps.current.data = data;
      return true;
    }

    const currentColumns = columns;
    const {
      columns: previousColumns
    } = preProps;

    if (previousColumns.length !== currentColumns.length) {
      calculateCollapsedColumnsOrder(currentColumns, columnsCollapseOrder);
      return true;
    }

    const prevColumnsConfig = JSON.stringify(previousColumns.map(column => ({
      key: column.key,
      label: column.label,
      headerCellComponentProps: column.headerCellComponentProps // eslint-disable-line react/prop-types

    })));
    const currentColumnsConfig = JSON.stringify(currentColumns.map(column => ({
      key: column.key,
      label: column.label,
      headerCellComponentProps: column.headerCellComponentProps // eslint-disable-line react/prop-types

    })));

    if (prevColumnsConfig !== currentColumnsConfig) {
      calculateCollapsedColumnsOrder(currentColumns, columnsCollapseOrder);
      return true;
    }
  };
  /**
   * Container function for handling responsive behavior
   * is the entry-point from component life-cycle: useEffect.
   * Calculate displayable column count.
   * Communicate to the parent on the number of collapsed columns.
   */


  const handleResponsiveness = () => {
    displayableColumnCountLocal.current = getDisplayableColumnsCount(); // Process collapse order if table has a collapse order config.

    if (tableHasCollapseOrder.current) {
      // Calculate final visible column and related parameters.
      const {
        displayableColumnCount,
        collapsedColumns: collapsedColumnsList,
        displayableColumns
      } = processColumnsOrder(columns, displayableColumnCountLocal.current, reorderedColumnsByCollapseOrder.current, flattenedCollapseOrder.current, minimumColumnsRequired); // Cache variables that are needed to set per instance.

      collapsedColumns.current = collapsedColumnsList;
      orderedColumns.current = displayableColumns; // Final values to communicate to table.

      displayableColumnCountLocal.current = displayableColumnCount;
      calculatedCollapsedColumns.current = collapsedColumnsList;
    } else {
      calculatedCollapsedColumns.current = columns.slice(displayableColumnCountLocal.current);
    } // Update state and communicate to parent (table).


    setDisplayableColumnCount(displayableColumnCountLocal.current);
    setColumnsMinWidthMeasured(state => {
      prevState.current.columnsMinWidthMeasured = state;
      return true;
    });
    const totalColumns = columns.length;

    if (totalColumns - displayableColumnCountLocal.current >= 0) {
      onCollapseColumns(calculatedCollapsedColumns.current, responsiveStyles.expandableStyles);
    }
  };

  const debouncedHandleResponsiveness = debounce(handleResponsiveness, RESIZE_DEBOUNCE_TIME_MS, {
    leading: true
  });
  useLayoutEffect(() => {
    calculateCollapsedColumnsOrder(columns, columnsCollapseOrder);
    const tableContainerNode = tableNodeRef.current.parentNode;
    const tableContainerTop = tableContainerNode && tableContainerNode.getBoundingClientRect().top;
    const tableContainerIsVisible = tableContainerNode && tableContainerNode.getClientRects().length;

    if (tableContainerTop < 0 && !tableContainerIsVisible) {
      /**
       * Default the Table container's width to 0 in cases where the Table's container is
       * visually hidden. In cases where Table's container is visually hidden (Tabs/Section),
       * the `offsetWidth` of
       * the Table's container is not accurate, so relying on the width to be obtained
       * when `ResizeObserver` is triggered. As soon as the visually hidden node is no longer hidden,
       * the width of this node changes with respect to its parent element in the DOM
       * which would cause `ResizeObserver` to trigger.
       */
      tableContainerWidth.current = 0;
    } else {
      tableContainerWidth.current = tableContainerNode.offsetWidth;
    } // If the Table's container is visible on mount, then run the responsive logic.


    if (tableContainerWidth.current !== 0) {
      tableContainerNodeIsVisible.current = true;
      handleResponsiveness();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);
  useLayoutEffect(() => {
    const {
      columnsCollapseOrder: prevColumnsCollapseOrder,
      data: prevData,
      paginatedData: prevPaginatedData
    } = prevProps.current;
    /**
     * Re-calculate the reorderedColumnsByCollapseOrder,
     * if `columnsCollapseOrder` changed.
     */

    if (prevColumnsCollapseOrder.join("") !== columnsCollapseOrder.join("")) {
      prevProps.current.columnsCollapseOrder = columnsCollapseOrder;
      calculateCollapsedColumnsOrder(columns, columnsCollapseOrder);
    }

    const currentDataChanged = !isSameData(prevData, data);
    const noWrapColLength = columns.filter(col => col.noWrap).length;
    const paginatedDataChanged = noWrapColLength > 0 && !isSameData(prevPaginatedData, paginatedData);

    if ((currentDataChanged || paginatedDataChanged) && columnsMinWidthMeasured) {
      /**
       * Setting `columnsMinWidthMeasured` as `false` to recalculate the columns
       * min width if its current value is `true` and if there is any change in
       * `data` or any change in `paginatedData` if one of columns have `noWrap`.
       * `setState` can be called immediately in useEffect()
       * as long as it is wrapped in a condition, or an infinite loop is caused.
       */
      setColumnsMinWidthMeasured(state => {
        prevState.current.columnsMinWidthMeasured = state;
        return false;
      });
      paginatedDataChanged && (prevProps.current.paginatedData = paginatedData);
      currentDataChanged && (prevProps.current.data = data);
    } else if (shouldHandleResponsiveness()) {
      handleResponsiveness();
    }

    const tableContainerNode = tableNodeRef.current.parentNode; // Listen for changes on the Table's container width to run the responsive logic.

    tableContainerWidthObserver.current = onWidthResize(tableContainerNode, containerWidth => {
      /**
       * We need to check if the width actually changed because ResizeObserver
       * fires the callback immediately after we start observing for changes, even if there was no change.
       * That is per spec.
       */
      const widthChanged = containerWidth !== tableContainerWidth.current; // Only run the responsive logic if the container is visible and its width actually changed.

      if (widthChanged && containerWidth !== 0) {
        tableContainerWidth.current = containerWidth;
        tableContainerNodeIsVisible.current = true;
        debouncedHandleResponsiveness();
      }
    });
    return () => {
      debouncedHandleResponsiveness && debouncedHandleResponsiveness.cancel && debouncedHandleResponsiveness.cancel();
      tableContainerWidthObserver.current && tableContainerWidthObserver.current.disconnect();
      tableContainerWidthObserver.current = null;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnsCollapseOrder, columns, data, displayableColumnCount, columnsMinWidthMeasured]);
  const visibleColumns = collapsedColumns.current && collapsedColumns.current.length > 0 && columnsCollapseOrder.length > 0 ? getVisibleColumnsOrderedByCollapseOrder() : columns.slice(0, displayableColumnCount);
  return columnsMinWidthMeasured ?
  /**
   * Render a table with only the columns that fit in the Table's container
   * without creating a horizontal scrollbar.
   */
  _jsx(TableComponent, _objectSpread({
    css: responsiveStyles,
    ref: tableNodeRef,
    onCollapseColumns: onCollapseColumns,
    columns: visibleColumns,
    data: data,
    paginatedData: paginatedData
  }, tableProps)) :
  /**
   * Render a table with all the available columns so we can calculate how many columns
   * will be shown/hidden based on column minimum widths and table's container width.
   */
  _jsx(TableComponent, _objectSpread(_objectSpread({
    ref: tableNodeRef,
    css: responsiveStyles,
    columns: columns,
    onCollapseColumns: onCollapseColumns,
    data: data,
    paginatedData: paginatedData
  }, tableProps), {}, {
    // width of 1px is required so we can calculate the minimum width of each column.
    style: {
      width: 1
    }
  }));
});
Responsive.propTypes = process.env.NODE_ENV !== "production" ? {
  columns: PropTypes.arrayOf(PropTypes.shape({
    /** The key to locate the value for the column inside each record */
    key: PropTypes.string.isRequired,

    /** The display label for the header or column information */
    label: PropTypes.string,

    /** The header content for the column */
    header: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

    /**
     * The alignment of column's cells. Can be `left`, `center` or `right`.
     * Defaults to `left`
     */
    align: PropTypes.oneOf(["left", "center", "right"]),

    /** Additional class name added to the header and body cells */
    className: PropTypes.string,

    /** Describes whether the text content should not wrap. Default to `false` */
    noWrap: PropTypes.bool,

    /** Additional styles for the header cell */
    style: PropTypes.object,
    // eslint-disable-line react/forbid-prop-types

    /** The content of the cell to use when the record value is empty */
    emptyValue: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

    /**
     * A function that will return a formatted version of the columns data.
     * It will receive one object argument with `cellContent` and `item`
     */
    formatter: PropTypes.func
  })).isRequired,

  /**
   * The Table component to be extended.
   */
  tableComponent: PropTypes.object.isRequired,
  // eslint-disable-line react/forbid-prop-types

  /**
   * The priority order at which the columns should be collapsed depending on the available viewport size.
   * This prop expects the column keys to be passed to it.
   * i.e `columnsCollapseOrder: ['columnAKey', 'columnBKey']`.
   * The order at which the columns collapse matches the order,
   * at which the column keys are passed to this prop.
   * The columns can also be collapsed as a group.
   * This can be achieved by grouping the columns in an array.
   * i.e `columnsCollapseOrder: [ ['columnAKey', 'columnBKey']]`
   */
  columnsCollapseOrder: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])),

  /**
   * The data to be consumed by the table. This attribute takes an array of objects,
   * which are considered the records to display.
   */
  data: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-line react/forbid-prop-types

  /**
   * callback function for the parent  on collapsed columns whn the table is responsive
   */
  onCollapseColumns: PropTypes.func
} : {};
Responsive.defaultProps = {
  onCollapseColumns: () => {},
  columnsCollapseOrder: [],
  data: []
};
/**
 * This is the actual HOC that we export. It returns a Table component wrapped
 * with the  responsiveness   defined in `Responsive` Component. If the table is
 * explicitly called to be un-responsive via  `fixed' prop, original table is returned
 */

export default function withResponsive(TableComponent) {
  const WrappedTable = /*#__PURE__*/React.forwardRef((props, ref) => {
    const {
      fixed,
      onCollapseColumns
    } = props,
          others = _objectWithoutProperties(props, _excluded2);

    if (fixed) {
      return _jsx(TableComponent, _objectSpread(_objectSpread({}, others), {}, {
        ref: ref
      }));
    } else {
      return _jsx(Responsive, _objectSpread(_objectSpread({}, others), {}, {
        ref: ref,
        onCollapseColumns: onCollapseColumns,
        tableComponent: TableComponent
      }));
    }
  }); // Set the displayName of the decorated component.

  WrappedTable.displayName = `withResponsive(${TableComponent.displayName || TableComponent.name})`;
  return WrappedTable;
}